import type { TaskState } from '../../api/rmf-client';
import { useNowTick } from '../../state/clock';
import { formatDuration } from '../../utils/date-format';
import { taskElapsedSec, taskRemaining, type RemainingView } from './task-timing';

/** The cells for the two task clocks; the rules are in `task-timing.ts`. */

/** Whether the task-time cell changes on its own (needs a ticking clock). */
function elapsedTicks(task: TaskState): boolean {
  return (
    task.durationSec == null &&
    (task.status === 'active' || task.status === 'interrupted') &&
    Boolean(task.startedAt)
  );
}

function ElapsedText({ task, nowMs }: { task: TaskState; nowMs: number }) {
  const sec = taskElapsedSec(task, nowMs);
  return <span data-testid="task-time">{sec === undefined ? '' : formatDuration(sec)}</span>;
}

function TickingElapsed({ task }: { task: TaskState }) {
  const now = useNowTick(1_000);
  return <ElapsedText task={task} nowMs={now} />;
}

/** Task time: ticks only for a running task, so a table of finished rows
 *  runs no timers. */
export function TaskElapsed({ task }: { task: TaskState }) {
  return elapsedTicks(task) ? (
    <TickingElapsed task={task} />
  ) : (
    <ElapsedText task={task} nowMs={Date.now()} />
  );
}

const REMAINING_TITLE: Record<RemainingView['kind'], string> = {
  live: 'Estimated time remaining (fleet manager’s estimate, counting down)',
  held: 'Estimated time remaining when the task was interrupted',
  duration: 'Not started yet: the estimated duration of the whole task',
};

function RemainingText({ task, nowMs }: { task: TaskState; nowMs: number }) {
  const view = taskRemaining(task, nowMs);
  if (!view) return <span data-testid="task-remaining">—</span>;
  const overrun = view.kind === 'live' && view.sec === 0;
  return (
    <span
      data-testid="task-remaining"
      title={overrun ? 'Past the fleet manager’s estimate; still running' : REMAINING_TITLE[view.kind]}
    >
      ~{formatDuration(view.sec)}
    </span>
  );
}

function TickingRemaining({ task }: { task: TaskState }) {
  const now = useNowTick(1_000);
  return <RemainingText task={task} nowMs={now} />;
}

/** Est. time remaining: counts down only for an active task with an
 *  estimate. */
export function TaskRemaining({ task }: { task: TaskState }) {
  const ticks = task.status === 'active' && task.estimatedRemainingSec != null;
  return ticks ? <TickingRemaining task={task} /> : <RemainingText task={task} nowMs={Date.now()} />;
}
